/**
 * Where a quoted span from a compile sits in the text as it stands RIGHT NOW.
 *
 * Every span in a `CompiledDirective` is an offset into the exact body that was compiled, and
 * the author keeps typing afterward. Nothing errors when the two drift apart — the offsets stay
 * perfectly valid numbers pointing at whatever now occupies them — so any consumer that trusts
 * them blindly gets silent nonsense: highlights sliced through the middle of unrelated words, or
 * a replacement spliced into a sentence nobody pointed at.
 *
 * One algorithm, two callers: the highlight layer uses it to drop or move marks as the text
 * changes, and the promotion flow uses it to find the sentence a rewrite replaces. They were
 * solving the same problem, and a second copy of this logic is exactly how the two would start
 * disagreeing about where a span is.
 */

export interface LocatableSpan {
    start: number
    end: number
    /** The body substring the compiler resolved — always verbatim, never the model's paraphrase
     * (sealab stores `body.slice(start, end)`, not the quote the LLM copied). That is what makes
     * it usable as a checksum for the offsets, and as a way to find them again. */
    quote: string
}

/**
 * The span's range in `body`, or null if it can no longer be placed.
 *
 * Three tiers, in decreasing order of certainty:
 *
 *   1. The offsets still slice out the quote — nothing relevant moved. The overwhelmingly common
 *      case, including every edit made AFTER the span.
 *   2. `compiledBody` is still present verbatim inside `body`, so the author only added text
 *      around it: every span shifts by one constant, recoverable exactly. This is what keeps the
 *      highlights attached when someone types a new line above them, instead of the whole set
 *      sliding out of register.
 *   3. The quote itself occurs exactly once — the text was edited in the middle, but this
 *      particular sentence survived intact and there is only one place it can be.
 *
 * Anything else returns null, and null means DROP. Two occurrences give no basis for choosing,
 * and a partially-edited sentence has no intact quote left to find; in both cases showing
 * nothing is right, because a highlight in the wrong place is a claim about the author's text
 * that isn't true.
 */
export function locateSpan(
    body: string,
    span: LocatableSpan,
    compiledBody?: string,
): { start: number; end: number } | null {
    if (!span.quote) return null
    if (body.slice(span.start, span.end) === span.quote) return { start: span.start, end: span.end }

    if (compiledBody) {
        const shift = body.indexOf(compiledBody)
        if (shift !== -1) {
            const start = span.start + shift
            const end = span.end + shift
            if (body.slice(start, end) === compiledBody.slice(span.start, span.end)) return { start, end }
        }
    }

    const first = body.indexOf(span.quote)
    if (first === -1) return null
    if (body.indexOf(span.quote, first + 1) !== -1) return null

    return { start: first, end: first + span.quote.length }
}
